import { createListenerMiddleware, type TypedStartListening } from '@reduxjs/toolkit';
import { setSegmentsCatalog } from '@/store/slices/segments/segmentsSlice';
import { applyLinkSegmentsLocal } from '@/store/slices/segments/segments.thunk';
import { selectSmartLink } from '@/store/slices/smartLink/smartLinkSlice';
import type { CONFIG_SCHEMA } from '@/store/slices/screenConfig/screenConfigSlice';
import type { AppDispatch, RootState } from '@/store/store';

/**
 * Re-asserts an active smart-link's segment override after the segments
 * catalog reloads.
 *
 * `fetchSegments` (bootstrap, mid-adoption refetches, etc.) dispatches
 * `setSegmentsCatalog`, which resets every segment's `isApplied` to the
 * server's persisted value — wiping the local-only override that
 * `applyLinkSegmentsLocal` (Task 2) applied to show a shared link's
 * segments. This listener watches for that reload and, while a smart-link
 * session is active, re-dispatches `applyLinkSegmentsLocal` with the link's
 * segments so the view-only override survives the reload.
 *
 * Scoped to By-Segment (SEGMENT_ONLY) screens only, mirroring
 * `appliedSegmentsMirror`'s screen guard: segments are a drawer-committed
 * filter dimension on other screens, not the raw applied set this override
 * targets.
 */
export const smartLinkReassert = createListenerMiddleware();

type AppStartListening = TypedStartListening<RootState, AppDispatch>;

const startListening = smartLinkReassert.startListening as AppStartListening;

startListening({
  actionCreator: setSegmentsCatalog,
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const mode = (state.screenConfig as CONFIG_SCHEMA | undefined)
      ?.segmentFilterModalMode;
    if (mode !== 'SEGMENT_ONLY') return; // segments are a drawer-committed filter dimension on other screens
    const { active, linkSegments } = selectSmartLink(state);
    if (!active || !linkSegments) return;
    listenerApi.dispatch(applyLinkSegmentsLocal(linkSegments));
  },
});
